/**
 * Coupon 创建表单 Modal —— 超管端和 Portal 端共用。
 *
 * 调用方传入 onSubmit（不同路径走不同 endpoint）。
 */
import { Alert, Button, Card, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconAlertCircle, IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CouponCreateRequest, CouponDuration } from '@mygpt/app/apis/services/dealer';

interface Props {
  opened: boolean;
  onClose: () => void;
  onSubmit: (req: CouponCreateRequest) => Promise<void>;
  /** 预留：将来支持 amount_off 时用来校验 70% 上限 */
  referencePriceId?: string;
}

const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const autoCode = (len = 16) =>
  Array.from({ length: len }, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join('');

export const CouponCreateModal = ({ opened, onClose, onSubmit, referencePriceId }: Props) => {
  const { t } = useTranslation();

  // 当前只开放百分比折扣（amount_off 需绑定具体 Price + 校验 70% 底线，留待后续）
  // 数值类输入保留 string|number 联合态，允许用户清空中间态键入新值
  const [percentOff, setPercentOff] = useState<number | string>(10);
  const [name, setName] = useState('');
  const [duration, setDuration] = useState<CouponDuration>('forever');
  const [durationMonths, setDurationMonths] = useState<number | string>(3);
  const [redeemBy, setRedeemBy] = useState<Date | null>(null);

  const [code, setCode] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState<number | string>(1);

  const [submitting, setSubmitting] = useState(false);
  const today = new Date();

  // 70% 底线提示（假设 0.30；后续可从 dealer 取）
  const MAX_RATE = 0.3;
  const percentNum = typeof percentOff === 'number' ? percentOff : Number(percentOff) || 0;
  const exceedsFloor = percentNum > MAX_RATE * 100;
  const maxRedNum = typeof maxRedemptions === 'number' ? maxRedemptions : Number(maxRedemptions) || 1;

  // v0.9.3 联动：指定顾客 → 隐藏「利用上限」(默认 1)；上限>1 → 隐藏「顾客邮箱」
  const showCustomerEmail = maxRedNum <= 1;
  const showMaxRedemptions = customerEmail.trim() === '';

  // 切到「指定顾客」时强制把上限收回 1
  useEffect(() => {
    if (customerEmail.trim() !== '' && maxRedNum !== 1) setMaxRedemptions(1);
  }, [customerEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setName('');
    setPercentOff(10);
    setDuration('forever');
    setDurationMonths(3);
    setRedeemBy(null);
    setCode('');
    setCustomerEmail('');
    setMaxRedemptions(1);
  };

  // v0.9.3 自动拼接券描述
  const summary = useMemo(() => {
    const nm = name.trim() || t('coupon.summary_unnamed');
    const dur =
      duration === 'forever'
        ? t('coupon.summary_duration_forever')
        : duration === 'once'
        ? t('coupon.summary_duration_once')
        : t('coupon.summary_duration_repeating', {
            months: typeof durationMonths === 'number' ? durationMonths : Number(durationMonths) || 1,
          });
    const expires = redeemBy
      ? t('coupon.summary_expires', { date: dayjs(redeemBy).format('YYYY/MM/DD HH:mm') })
      : t('coupon.summary_no_expiry');
    const customerPhrase = customerEmail.trim()
      ? t('coupon.summary_customer_specific', { email: customerEmail.trim() })
      : t('coupon.summary_customer_any');
    const usage = t('coupon.summary_max', { n: maxRedNum });
    return t('coupon.summary_template', {
      name: nm,
      percent: percentNum,
      duration: dur,
      expires,
      customer: customerPhrase,
      usage,
    });
  }, [name, percentNum, duration, durationMonths, redeemBy, customerEmail, maxRedNum, t]);

  const handleSubmit = async () => {
    if (exceedsFloor) return;
    setSubmitting(true);
    try {
      const req: CouponCreateRequest = {
        name,
        percent_off: percentNum,
        currency: 'jpy',
        duration,
        duration_in_months:
          duration === 'repeating'
            ? typeof durationMonths === 'number'
              ? durationMonths
              : Number(durationMonths) || 1
            : undefined,
        redeem_by: redeemBy ? redeemBy.toISOString() : undefined,
        reference_price_id: referencePriceId,
        promotion_code: {
          code: code || undefined,
          customer_email: customerEmail || undefined,
          max_redemptions: maxRedNum,
          // 与 redeem_by 共用同一个「有效期」字段，业务上一码一过期
          expires_at: redeemBy ? redeemBy.toISOString() : undefined,
          first_time_transaction: false,
        },
      };

      await onSubmit(req);
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('coupon.issue')} size="lg">
      <Stack>
        <TextInput label={t('coupon.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} required />

        <NumberInput
          label={t('coupon.percent_off')}
          suffix=" %"
          min={1}
          max={100}
          value={percentOff}
          onChange={setPercentOff}
        />

        {exceedsFloor && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {t('coupon.floor_warning_pct', { max: 30 })}
          </Alert>
        )}

        <Select
          label={t('coupon.duration.label')}
          data={[
            { value: 'once', label: t('coupon.duration.once') },
            { value: 'forever', label: t('coupon.duration.forever') },
            { value: 'repeating', label: t('coupon.duration.repeating') },
          ]}
          value={duration}
          onChange={(v) => setDuration((v as CouponDuration) || 'forever')}
        />
        {duration === 'repeating' && (
          <NumberInput
            label={t('coupon.duration_months_label')}
            min={1}
            max={36}
            value={durationMonths}
            onChange={setDurationMonths}
          />
        )}
        <DateTimePicker
          label={t('coupon.redeem_by')}
          description={t('coupon.redeem_by_hint')}
          valueFormat="YYYY/MM/DD HH:mm"
          placeholder="YYYY/MM/DD HH:mm"
          value={redeemBy}
          onChange={(v) => setRedeemBy(v ? new Date(v) : null)}
          minDate={today}
          clearable
        />

        <Group align="flex-end">
          <TextInput
            label={t('coupon.code')}
            placeholder={t('coupon.code_placeholder') as string}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value.toUpperCase())}
            flex={1}
          />
          <Button variant="light" leftSection={<IconRefresh size={14} />} onClick={() => setCode(autoCode(16))}>
            {t('coupon.code_auto_generate')}
          </Button>
        </Group>
        {showCustomerEmail && (
          <TextInput
            label={t('coupon.customer_email')}
            description={t('coupon.customer_email_hint')}
            placeholder="user@example.com"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.currentTarget.value)}
          />
        )}
        {showMaxRedemptions && (
          <NumberInput
            label={t('coupon.max_redemptions')}
            min={1}
            value={maxRedemptions}
            onChange={setMaxRedemptions}
          />
        )}

        <Card withBorder bg="blue.0" p="sm">
          <Group gap="xs" align="flex-start" wrap="nowrap">
            <IconInfoCircle size={16} style={{ marginTop: 3, flexShrink: 0 }} />
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Text>
          </Group>
        </Card>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('coupon.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={exceedsFloor || !name} loading={submitting}>
            {t('coupon.issue')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
